import { useState, useEffect, useRef } from 'react';
import ClientLayout from '@/components/feature/ClientLayout';
import { supabase } from '@/lib/supabase';
import { createNotification } from '@/lib/notifications';
import { useClientProject } from '@/hooks/useClientProject';
import { useAuth } from '@/hooks/useAuth';
import FileUploader from '@/components/feature/FileUploader';
import { useTranslation } from 'react-i18next';
import { FILE_OPEN_ERROR_MESSAGE, openProjectFileInNewTab, useSignedFileUrls } from '@/lib/storageSignedUrl';

interface QuestionItem {
  id: string;
  question_title: string;
  body: string;
  created_at: string;
  status: string;
}

interface ReplyItem {
  id: string;
  question_id: string;
  author_user_id: string;
  body: string;
  created_at: string;
}

interface AttachmentItem {
  id: string;
  question_id: string;
  file_id: string;
  file_name: string;
  file_url: string;
  file_type: string | null;
  file_size: string | number | null;
  storage_bucket?: string | null;
  storage_path?: string | null;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-NZ', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function isImage(ext: string | null): boolean {
  if (!ext) return false;
  return ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext.toLowerCase());
}

export default function ClientQuestions() {
  const { t } = useTranslation();
  const { selectedProject, projectId } = useClientProject();
  const { user } = useAuth();
  const [questions, setQuestions] = useState<QuestionItem[]>([]);
  const [replies, setReplies] = useState<ReplyItem[]>([]);
  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
  const attUrls = useSignedFileUrls(attachments);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [questionTitle, setQuestionTitle] = useState('');
  const [questionBody, setQuestionBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Attachment upload state — records from FileUploader
  const [uploadedAttachments, setUploadedAttachments] = useState<Array<{ id: string; file_name: string; file_url: string; file_type: string | null }>>([]);
  const [showFileUploader, setShowFileUploader] = useState(false);

  // Lightbox
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  useEffect(() => {
    if (projectId) loadQuestions(projectId);
  }, [projectId]);

  async function loadQuestions(pid: string) {
    setLoading(true);
    const { data: qData, error: qErr } = await supabase
      .from('jphph_client_questions')
      .select('id, question_title, body, created_at, status')
      .eq('project_id', pid)
      .order('created_at', { ascending: false });

    if (qErr) console.error('Error loading questions:', qErr);

    const questionsList = (qData as QuestionItem[]) ?? [];
    setQuestions(questionsList);

    const ids = questionsList.map((q) => q.id);

    // Load replies
    if (ids.length > 0) {
      const { data: rData, error: rErr } = await supabase
        .from('jphph_client_question_replies')
        .select('*')
        .in('question_id', ids)
        .order('created_at', { ascending: true });
      if (rErr) console.error('Error loading replies:', rErr);
      setReplies((rData as ReplyItem[]) ?? []);

      // Load attachments
      const { data: aData, error: aErr } = await supabase
        .from('jphph_client_question_attachments')
        .select('id, question_id, file_id')
        .in('question_id', ids);
      if (aErr) console.error('Error loading attachments:', aErr);

      const attachRows = (aData ?? []) as { id: string; question_id: string; file_id: string }[];
      if (attachRows.length > 0) {
        const fileIds = attachRows.map((a) => a.file_id);
        const { data: fData } = await supabase
          .from('jphph_project_files')
          .select('id, file_name, file_url, file_type, file_size, storage_bucket, storage_path')
          .in('id', fileIds);
        const filesById = new Map(
          ((fData ?? []) as AttachmentItem[]).map((f) => [f.id, f])
        );
        const merged: AttachmentItem[] = attachRows.map((a) => {
          const f = filesById.get(a.file_id);
          return {
            id: a.id,
            question_id: a.question_id,
            file_id: a.file_id,
            file_name: f?.file_name || 'File',
            file_url: f?.file_url || '',
            file_type: f?.file_type || null,
            file_size: f?.file_size || null,
            storage_bucket: f?.storage_bucket ?? null,
            storage_path: f?.storage_path ?? null,
          };
        });
        setAttachments(merged);
      } else {
        setAttachments([]);
      }
    } else {
      setReplies([]);
      setAttachments([]);
    }

    setLoading(false);
  }

  const handleFileUploaderComplete = (records: Array<{ id: string; file_name: string; file_url: string; file_type: string | null; file_size: string | number | null }>) => {
    setUploadedAttachments((prev) => [...prev, ...records]);
    setShowFileUploader(false);
  };

  const removeAttachment = (id: string) => {
    setUploadedAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!questionTitle.trim() || !questionBody.trim() || !projectId || !user) return;
    setSubmitting(true);

    // Insert question
    const { data: inserted, error: insertErr } = await supabase
      .from('jphph_client_questions')
      .insert({
        project_id: projectId,
        client_user_id: user.id,
        question_title: questionTitle.trim(),
        body: questionBody.trim(),
        status: 'open',
      })
      .select()
      .maybeSingle();

    if (insertErr) {
      console.error('Insert question failed:', insertErr);
      setSubmitting(false);
      return;
    }

    const questionId = inserted?.id;

    // Link uploaded attachments to question
    if (questionId && uploadedAttachments.length > 0) {
      const linkRows = uploadedAttachments.map((rec) => ({
        question_id: questionId,
        project_id: projectId,
        file_id: rec.id,
      }));
      const { error: linkErr } = await supabase
        .from('jphph_client_question_attachments')
        .insert(linkRows);
      if (linkErr) console.error('Link attachments failed:', linkErr);
    }

    // Notify management
    await createNotification({
      project_id: projectId,
      notification_type: 'question',
      title: 'New client question',
      body: questionTitle.trim(),
      target_role: 'management',
      link_path: `/management/projects/${projectId}`,
      target_type: 'question',
      target_id: questionId ?? null,
      created_by: user.id,
    });

    // Reset form
    setQuestionTitle('');
    setQuestionBody('');
    setUploadedAttachments([]);
    setShowForm(false);
    setSubmitting(false);
    await loadQuestions(projectId);
  };

  const getReplies = (questionId: string) =>
    replies.filter((r) => r.question_id === questionId);

  const getAttachments = (questionId: string) =>
    attachments.filter((a) => a.question_id === questionId);

  return (
    <ClientLayout title="Questions">
      <div className="px-4 lg:px-8 pt-5 pb-10 max-w-3xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-[15px] font-bold text-[#1A1A1A]">{t('client.questions.yourQuestions')}</h2>
            <p className="text-[#9CA3AF] text-xs mt-0.5">
              {selectedProject?.project_name ? `${selectedProject.project_name} · ` : ''}
              {t('client.questions.askDesc')}
            </p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-1.5 bg-[#E8621A] text-white px-3 py-1.5 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            {t('client.questions.ask')}
          </button>
        </div>

        {/* Direction indicator */}
        <div className="flex items-center gap-2 mb-4">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100">
            <i className="ri-user-line text-[10px] text-[#374151]"></i>
            <span className="text-[10px] font-bold text-[#374151]">{t('client.questions.client')}</span>
          </div>
          <i className="ri-arrow-right-line text-[#9CA3AF] text-sm"></i>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FEF0E7]">
            <i className="ri-building-2-line text-[10px] text-[#E8621A]"></i>
            <span className="text-[10px] font-bold text-[#E8621A]">{t('client.questions.management')}</span>
          </div>
        </div>

        {/* Ask form */}
        {showForm && (
          <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-4">
            <form onSubmit={handleSubmit}>
              <label className="block text-sm font-semibold text-[#374151] mb-2">
                {t('client.questions.questionTitle')}
              </label>
              <input
                type="text"
                value={questionTitle}
                onChange={(e) => setQuestionTitle(e.target.value)}
                placeholder={t('client.questions.questionPlaceholder')}
                maxLength={120}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#374151] placeholder-[#9CA3AF] focus:outline-none focus:border-[#E8621A] transition-colors mb-3"
                required
              />
              <label className="block text-sm font-semibold text-[#374151] mb-2">
                {t('client.questions.details')}
              </label>
              <textarea
                value={questionBody}
                onChange={(e) => setQuestionBody(e.target.value)}
                placeholder={t('client.questions.detailsPlaceholder')}
                rows={3}
                maxLength={500}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#374151] placeholder-[#9CA3AF] focus:outline-none focus:border-[#E8621A] transition-colors resize-none"
                required
              />

              {/* Attachments */}
              <div className="mt-3">
                <label className="block text-sm font-semibold text-[#374151] mb-2">
                  {t('client.questions.attachments')}
                </label>

                {/* Already uploaded attachments preview */}
                {uploadedAttachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {uploadedAttachments.map((att) => {
                      const isImg = isImage(att.file_type);
                      return (
                        <div key={att.id} className="relative bg-gray-50 rounded-xl p-2 flex items-center gap-2 border border-gray-100">
                          {isImg ? (
                            <div className="w-8 h-8 rounded-md bg-gray-200 flex items-center justify-center overflow-hidden">
                              <img src={att.file_url} alt={att.file_name} className="w-full h-full object-cover" />
                            </div>
                          ) : (
                            <div className="w-8 h-8 rounded-md bg-[#FEF0E7] flex items-center justify-center">
                              <i className="ri-file-pdf-line text-[#E8621A] text-sm"></i>
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="text-[#374151] text-xs font-medium truncate max-w-[140px]">{att.file_name}</p>
                            <p className="text-[#059669] text-[10px]">Uploaded</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeAttachment(att.id)}
                            className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-200 cursor-pointer text-[#9CA3AF]"
                          >
                            <i className="ri-close-line text-sm"></i>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => setShowFileUploader(true)}
                  className="w-full py-2.5 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center gap-2 cursor-pointer hover:border-[#E8621A] hover:bg-[#FEF0E7] transition-colors"
                >
                  <i className="ri-add-line text-[#9CA3AF] text-sm"></i>
                  <span className="text-[#9CA3AF] text-sm">{t('client.questions.addFiles')}</span>
                </button>

                <p className="text-[#9CA3AF] text-[10px] mt-1.5">{t('client.questions.filesUploaded')}</p>
              </div>

              <div className="flex items-center justify-between mt-3">
                <span className="text-[#9CA3AF] text-xs">
                  {questionBody.length}/500
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setQuestionTitle('');
                      setQuestionBody('');
                      setUploadedAttachments([]);
                    }}
                    className="px-4 py-2 rounded-lg border border-gray-200 text-[#374151] text-sm cursor-pointer whitespace-nowrap"
                  >
                    {t('client.instructions.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={submitting || !questionTitle.trim() || !questionBody.trim()}
                    className="px-4 py-2 rounded-lg bg-[#E8621A] text-white text-sm font-semibold cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    {submitting ? (
                      <i className="ri-loader-4-line animate-spin"></i>
                    ) : (
                      t('client.questions.send')
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <i className="ri-loader-4-line animate-spin text-[#E8621A] text-2xl"></i>
          </div>
        )}

        {/* Questions list */}
        {!loading && (
          <div className="space-y-3">
            {questions.length === 0 && (
              <div className="text-center py-12">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 text-[#D1D5DB]">
                  <i className="ri-question-answer-line text-5xl"></i>
                </div>
                <div className="text-[#374151] font-semibold mb-1">{t('client.questions.noQuestions')}</div>
                <div className="text-[#9CA3AF] text-sm">
                  {t('client.questions.noQuestionsDesc')}
                </div>
              </div>
            )}

            {questions.map((q) => {
              const qReplies = getReplies(q.id);
              const isAnswered = q.status === 'answered' || qReplies.length > 0;
              const qAttachments = getAttachments(q.id);
              return (
                <div key={q.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  <div className="p-4">
                    {/* Client question */}
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-user-line text-[#374151] text-sm"></i>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-[10px] font-bold text-[#374151]">{t('client.questions.clientQuestion')}</span>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isAnswered ? 'bg-[#F0FDF4] text-[#059669]' : 'bg-[#FEF0E7] text-[#E8621A]'}`}>
                            {isAnswered ? t('client.questions.answered') : t('client.questions.open')}
                          </span>
                        </div>
                        <p className="text-[#1A1A1A] text-sm font-semibold leading-snug">{q.question_title}</p>
                        <p className="text-[#6B7280] text-sm leading-relaxed mt-1">{q.body}</p>
                        <p className="text-[#9CA3AF] text-xs mt-1.5">{fmtDate(q.created_at)}</p>
                      </div>
                    </div>

                    {/* Attachments */}
                    {qAttachments.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {qAttachments.map((att) => {
                          const attDisplayUrl = attUrls[att.id] || att.file_url;
                          if (isImage(att.file_type)) {
                            return (
                              <button
                                key={att.id}
                                onClick={() => setLightboxUrl(attDisplayUrl)}
                                className="relative w-24 h-20 rounded-xl overflow-hidden border border-gray-100 cursor-pointer group"
                              >
                                <img
                                  src={attDisplayUrl}
                                  alt={att.file_name}
                                  loading="lazy"
                                  className="w-full h-full object-cover object-center"
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                  <i className="ri-zoom-in-line text-white opacity-0 group-hover:opacity-100 text-lg transition-opacity"></i>
                                </div>
                              </button>
                            );
                          }
                          return (
                            <button
                              key={att.id}
                              type="button"
                              onClick={() => {
                                void (async () => {
                                  const result = await openProjectFileInNewTab(att);
                                  if (!result.ok) {
                                    // eslint-disable-next-line no-console
                                    console.warn('[ClientQuestions] open attachment failed', result.error || FILE_OPEN_ERROR_MESSAGE);
                                  }
                                })();
                              }}
                              className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2 border border-gray-100 hover:border-gray-200 transition-colors cursor-pointer"
                            >
                              <div className="w-8 h-8 rounded-lg bg-[#FEF0E7] flex items-center justify-center flex-shrink-0">
                                <i className="ri-file-pdf-line text-[#E8621A] text-sm"></i>
                              </div>
                              <div className="min-w-0">
                                <p className="text-[#374151] text-xs font-medium truncate max-w-[140px]">{att.file_name}</p>
                                <p className="text-[#9CA3AF] text-[10px]">PDF</p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Management answers */}
                    {qReplies.length > 0 ? (
                      <div className="space-y-2">
                        {qReplies.map((reply) => (
                          <div key={reply.id} className="bg-[#FEF0E7] rounded-xl p-3">
                            <div className="flex items-center gap-2 mb-1.5">
                              <div className="w-6 h-6 rounded-lg bg-[#E8621A] flex items-center justify-center">
                                <i className="ri-building-2-line text-white text-[10px]"></i>
                              </div>
                              <span className="text-[#E8621A] text-xs font-bold">{t('client.questions.japanHomesReply')} · {fmtDate(reply.created_at)}</span>
                            </div>
                            <p className="text-[#374151] text-sm leading-relaxed">{reply.body}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-2">
                        <i className="ri-time-line text-[#9CA3AF] text-sm"></i>
                        <span className="text-[#9CA3AF] text-sm">{t('client.questions.awaitingResponse')}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* File Uploader Modal */}
      {showFileUploader && projectId && user && (
        <FileUploader
          projectId={projectId}
          currentUserId={user.id}
          open={showFileUploader}
          onClose={() => setShowFileUploader(false)}
          onUploadComplete={handleFileUploaderComplete}
          accountType="client"
          category="mixed"
          allowMultiple={true}
          allowCamera={true}
          allowPdf={true}
          enablePerFileCaption={true}
          relatedType="question"
          title="Add Attachments"
        />
      )}

      {/* Image Lightbox */}
      {lightboxUrl && (
        <div className="fixed inset-0 bg-black/95 z-[70] flex flex-col" onClick={() => setLightboxUrl(null)}>
          <div className="flex items-center justify-end px-4 py-4 flex-shrink-0">
            <button
              className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 text-white cursor-pointer"
              onClick={() => setLightboxUrl(null)}
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center px-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <img src={lightboxUrl} alt="Preview" className="max-w-full max-h-full rounded-2xl object-contain" />
          </div>
          <div className="py-4"></div>
        </div>
      )}
    </ClientLayout>
  );
}
